// ThemeContext.js
import React, {createContext, useState, useContext, useEffect} from "react";
import {ThemeProvider as StyledThemeProvider} from "styled-components";
import {
    blueDarkTheme,
    blueTheme,
    brownTheme,
    dangerDarkTheme,
    dangerTheme,
    darkPinkTheme,
    defaultTheme,
    pinkTheme,
} from "../styles/theme";

const ThemeContext = createContext();

export const ThemeProvider = ({children}) => {
    const [theme, setTheme] = useState({});
    const [isUpdating, setIsUpdating] = useState(false);
    // Helper function to convert hex to rgba
    const hexToRgba = (hex, alpha = 0.15) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // Helper function to create dynamic theme
    const createDynamicTheme = (color) => ({
        light: {
            primary: color,
            primary50: hexToRgba(color, 0.15),
        },
    });

    // Fast theme update using CSS custom properties
    const updateCSSVariables = (color) => {
        const root = document.documentElement;
        const rgba = hexToRgba(color, 0.15);
        
        // Update CSS custom properties instantly (no React re-render)
        root.style.setProperty('--theme-primary', color);
        root.style.setProperty('--theme-primary-50', rgba);
        root.style.setProperty('--primary', color);
        root.style.setProperty('--primary-50', rgba);
        root.style.setProperty('--link-color', color);
        
        // Update any other theme-related CSS variables that might be used
        root.style.setProperty('--primary-color', color);
        root.style.setProperty('--primary-color-light', rgba);
    };

    // Debounced React state update to reduce re-renders
    const updateReactTheme = (newTheme) => {
        if (isUpdating) return; // Prevent multiple simultaneous updates
        
        setIsUpdating(true);
        
        // Use setTimeout to batch the React state update
        setTimeout(() => {
            switch (newTheme) {
            case "#7DF500": {
                setTheme(defaultTheme);
                localStorage.setItem("themeColor", newTheme);
                break;
            }
            case "#3392CC": {
                setTheme(blueTheme);
                localStorage.setItem("themeColor", newTheme);
                break;
            }
            case "#E4042C": {
                setTheme(dangerTheme);
                localStorage.setItem("themeColor", newTheme);
                break;
            }
            case "#836529": {
                setTheme(brownTheme);
                localStorage.setItem("themeColor", newTheme);
                break;
            }
            case "#e404bf": {
                setTheme(pinkTheme);
                localStorage.setItem("themeColor", newTheme);
                break;
            }
            case "#0E79BF": {
                setTheme(blueDarkTheme);
                localStorage.setItem("themeColor", newTheme);
                break;
            }
            case "#DE5B9E": {
                setTheme(darkPinkTheme);
                localStorage.setItem("themeColor", newTheme);
                break;
            }
            case "#C6164F": {
                setTheme(dangerDarkTheme);
                localStorage.setItem("themeColor", newTheme);
                break;
            }

            default:
                // Handle custom colors by creating dynamic theme
                if (newTheme && newTheme.startsWith('#')) {
                    const dynamicTheme = createDynamicTheme(newTheme);
                    setTheme(dynamicTheme);
                    localStorage.setItem("themeColor", newTheme);
                }
                break;
            }
            setIsUpdating(false);
        }, 50); // Small delay to batch updates
    };

    const toggleTheme = (newTheme) => {
        // First, update CSS variables for instant visual feedback
        updateCSSVariables(newTheme);
        
        // Then update React state with debouncing
        updateReactTheme(newTheme);
    };

    useEffect(() => {
        const savedColor = localStorage.getItem("themeColor");
        if (savedColor) {
            // Set CSS variables immediately on load
            updateCSSVariables(savedColor);
            toggleTheme(savedColor);
        } else {
            // Set default color CSS variables
            updateCSSVariables("#7DF500");
            setTheme(defaultTheme);
        }
    }, []);

    return (
        <ThemeContext.Provider value={{toggleTheme}}>
            <StyledThemeProvider theme={theme}>{children}</StyledThemeProvider>
        </ThemeContext.Provider>
    );
};

export const useTheme = () => useContext(ThemeContext);
